import { useState } from 'react';
import { useLang } from '@/context/LanguageContext';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowRight, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: '', email: '', company: '', service: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('success');
      setForm({ name: '', email: '', company: '', service: '', message: '' });
    } catch {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const serviceOptions = t('contact.serviceOptions') || [];

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-32 bg-zinc-950">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          <div>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-4"
            >
              {t('contact.label')}
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-5xl lg:text-6xl font-normal tracking-normal text-purple-400 leading-none mb-6"
              style={{ fontFamily: "'Great Vibes', cursive" }}
            >
              {t('contact.title')}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-base lg:text-lg font-manrope text-zinc-400 leading-relaxed"
            >
              {t('contact.desc')}
            </motion.p>

            {/* Contact info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="mt-10 space-y-6"
            >
              <div data-testid="contact-address">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-2">
                  {t('contact.addressLabel')}
                </p>
                <p className="text-base font-manrope text-zinc-300">
                  42, avenue Montaigne<br />75008 Paris
                </p>
              </div>
              <div data-testid="contact-whatsapp">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-2">
                  WhatsApp
                </p>
                <div className="space-y-2">
                  <a
                    href="https://wa.me/33611563617"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="whatsapp-1"
                    className="flex items-center gap-3 text-base font-manrope text-zinc-300 hover:text-zinc-50 transition-colors"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-emerald-400 shrink-0"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    +33 (0) 6 11 56 36 17
                  </a>
                  <a
                    href="https://wa.me/33766560698"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="whatsapp-2"
                    className="flex items-center gap-3 text-base font-manrope text-zinc-300 hover:text-zinc-50 transition-colors"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-emerald-400 shrink-0"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    +33 (0) 7 66 56 06 98
                  </a>
                </div>
              </div>
              <div data-testid="contact-email-info">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-2">
                  Email
                </p>
                <a
                  href="mailto:contact@madisonfred.com"
                  data-testid="contact-email-link"
                  className="flex items-center gap-3 text-base font-manrope text-zinc-300 hover:text-zinc-50 transition-colors"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-zinc-400 shrink-0"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                  contact@madisonfred.com
                </a>
              </div>
            </motion.div>
          </div>

          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="space-y-6"
          >
            <Input
              data-testid="contact-name"
              placeholder={t('contact.name')}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              className="bg-transparent border-white/20 text-zinc-50 placeholder:text-zinc-600 rounded-none h-12 focus:border-white focus:ring-white"
            />
            <Input
              data-testid="contact-email"
              type="email"
              placeholder={t('contact.email')}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              className="bg-transparent border-white/20 text-zinc-50 placeholder:text-zinc-600 rounded-none h-12 focus:border-white focus:ring-white"
            />
            <Input
              data-testid="contact-company"
              placeholder={t('contact.company')}
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              className="bg-transparent border-white/20 text-zinc-50 placeholder:text-zinc-600 rounded-none h-12 focus:border-white focus:ring-white"
            />
            <Select onValueChange={(val) => setForm({ ...form, service: val })}>
              <SelectTrigger
                data-testid="contact-service"
                className="bg-transparent border-white/20 text-zinc-50 rounded-none h-12 focus:ring-white [&>span]:text-zinc-600"
              >
                <SelectValue placeholder={t('contact.service')} />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                {serviceOptions.map((opt, i) => (
                  <SelectItem
                    key={i}
                    value={opt}
                    className="text-zinc-50 focus:bg-zinc-800 focus:text-zinc-50"
                  >
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Textarea
              data-testid="contact-message"
              placeholder={t('contact.message')}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              required
              rows={5}
              className="bg-transparent border-white/20 text-zinc-50 placeholder:text-zinc-600 rounded-none focus:border-white focus:ring-white resize-none"
            />

            <button
              data-testid="contact-submit"
              type="submit"
              disabled={loading}
              className="group w-full bg-white text-black py-4 text-sm font-semibold uppercase tracking-wider hover:bg-zinc-200 transition-colors duration-300 flex items-center justify-center gap-3 disabled:opacity-50"
            >
              {loading ? t('contact.sending') : t('contact.send')}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>

            {status === 'success' && (
              <div data-testid="contact-success" className="flex items-center gap-2 text-emerald-400 text-sm">
                <CheckCircle size={16} />
                {t('contact.success')}
              </div>
            )}
            {status === 'error' && (
              <p data-testid="contact-error" className="text-red-400 text-sm">
                {t('contact.error')}
              </p>
            )}
          </motion.form>
        </div>
      </div>
    </section>
  );
}
